import React, { Component, ReactNode } from 'react';
import styles from './index.css';
import classNames from 'classnames';
import Tooltip from '@tencent/dui/lib/components/Tooltip';
import { ActionType } from '../../../editor/src/js/constants/common-action-type';
import { getPlatformShortcut } from '../../../../js/utils';
import { DropdowFrom } from '@src/framework/editor/src/js/constants/toolbar-data';
import Dropdown from '@tencent/dui/lib/components/Dropdown';
import type { TPlacement } from '@tencent/dui/lib/components/Tooltip/interface';

interface DropDownContentProps {
    idPreFix: string;
    id: string;
    dropdown: { icon?: string };
    dropdownHover?: boolean;
    hideDropDownIcon?: boolean;
    from?: string;
}

const DropDownContent: React.FC<DropDownContentProps> = ({
    idPreFix,
    id,
    dropdown,
    dropdownHover,
    hideDropDownIcon,
    from,
}) => {
    if (dropdown?.icon && id !== ActionType.INSERT_FORM
    && !(from === DropdowFrom.DOUBLEITEM) && !hideDropDownIcon) {
        return (
            <img
                id={`${idPreFix}toolbar-dropdown-${id}`}
                className={
                    classNames({
                        [styles.dropIcon]: true,
                        [styles.hover]: dropdownHover,
                        [styles.dropIconMargin]: id === ActionType.FONT || id === ActionType.FONTSIZE,
                    })
                }
                src={dropdown.icon}
            />
        );
    }
    return null;
};

interface DropdownColorPanelProps {
    idPreFix: string;
    id: string;
    dropdown: { icon?: string };
    dropdownHover?: boolean;
    dropdownHeight: number;
    onVisibleChange: (visible: boolean) => void;
    containerRef: HTMLElement | null;
    dropdownContent?: any;
}

const DropdownColorPanel: React.FC<DropdownColorPanelProps> = ({
    idPreFix,
    id,
    dropdown,
    dropdownHover,
    dropdownHeight,
    onVisibleChange,
    containerRef,
    dropdownContent: DropdownContent,
}) => {
    const rect = containerRef?.getBoundingClientRect?.();
    const style: React.CSSProperties = {
        height: dropdownHeight,
    };
    if (rect?.left) {
        style.left = rect?.left;
    }
    if (rect?.bottom) {
        const newTop = computeDropdownContentOffsetIfOverflowScreen(dropdownHeight, rect?.bottom);
        if (rect?.bottom !== newTop) {
            style.top = newTop;
        }
    }

    return (
        <div
            style={{
                height: 20,
            }}
            onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
            }}
        >
            <Dropdown
                dropContentStyle={style}
                style={{
                    alignSelf: 'center',
                    width: '10px',
                    height: '20px',
                }}
                onVisibleChange={onVisibleChange}
                containerFollowVisible
                dropContent={DropdownContent}
            >
                <img
                    id={`${idPreFix}toolbar-dropdown-${id}`}
                    className={
                        classNames({
                            [styles.dropIcon]: true,
                            [styles.dropIconVertical]: true,
                            [styles.hover]: dropdownHover,
                            [styles.dropIconMargin]: id === ActionType.FONT || id === ActionType.FONTSIZE,
                        })
                    }
                    src={dropdown.icon}
                />
            </Dropdown>
        </div>
    );
};

// 如果上下都放不下dropdown，就向上偏移
function computeDropdownContentOffsetIfOverflowScreen(dropdownHeight: number, dropdownTop: number): number {
    const { offsetHeight } = document.body;
    let top = dropdownTop;
    if (dropdownTop < dropdownHeight && (offsetHeight - dropdownTop) < dropdownHeight) {
        top = offsetHeight - dropdownHeight;
    }
    return top;
}

interface ToolBarItemProps {
    id: string;
    tipMsg?: string;
    selected?: boolean;
    disabled: boolean;
    handleClick?: (id: string, value: any, options: { e: React.MouseEvent<HTMLDivElement>, clickFrom: string }) => void;
    handleDoubleClick?: (id: string) => void;
    canDoubleClick?: boolean;
    tipPlacement?: TPlacement;
    isDoubleMode?: boolean;
    notClick?: boolean;
    value?: any;
    style?: React.CSSProperties;
    idPreFix?: string;
    dropdown: { icon?: string; [key: string]: any; };
    dropdownHover?: boolean;
    isColorPanel?: boolean;
    dropdownHeight?: number;
    [key: string]: any;
}

interface ToolBarItemState {
    dropdownVisible: boolean;
    tooltipVisible: boolean;
    value: any;
}
// todo: 逐步拆开这个组件，以前是复用单双工具栏所以很复杂，现在只有quick setting用就没必要搞着这么多东西了
export const hocToolbarItem = (TitleComponent: React.FC<ToolBarItemProps>) => (
    class ToolBarItem extends Component<ToolBarItemProps, ToolBarItemState> {
        public state: ToolBarItemState = {
            dropdownVisible: false,
            tooltipVisible: false,
            value: this.props.value,
        };

        private count = 0;
        private ref: HTMLElement | null;

        // eslint-disable-next-line react/no-deprecated
        public componentWillReceiveProps(nextProps: ToolBarItemProps) {
            this.setState({ value: nextProps.value });
        }

        public render(): ReactNode {
            const {
                id,
                selected,
                disabled,
                tipMsg,
                style = {},
                tipPlacement = 'bottom',
                isColorPanel = false,
                dropdownHeight,
                isDoubleMode,
                notClick,
            } = this.props;
            const idPreFix = this.props.idPreFix || '';
            const itemException = [ActionType.ADD_SLIDE, ActionType.FONT, ActionType.FONTSIZE];
            let isExceptionIcon = false;
            if (isDoubleMode) {
                isExceptionIcon = itemException.includes(id);
            } else {
                isExceptionIcon = id === ActionType.ADD_SLIDE;
            }

            const itemClass = classNames({
                [styles.item]: !isExceptionIcon,
                [styles.hover]: !isExceptionIcon && !notClick,
                [styles.selected]: !isExceptionIcon && (selected || this.state.value === true),
                [styles.gray]: disabled,
            });

            return (
                <Tooltip
                    title={getPlatformShortcut(tipMsg)}
                    placement={tipPlacement}
                    offset={this.tooltipOffset(id)}
                    visible={this.state.tooltipVisible && !selected && !this.state.dropdownVisible}
                    disabled={!tipMsg}
                    overlayStyle={{ zIndex: 99999 }}
                    onVisibleChange={this.handleVisibleChange}
                    style={{ margin: 'auto 0' }}
                    hoverDelay={100}
                >
                    <div
                        ref={ref => this.ref = ref}
                        id={`${idPreFix}toolbar-${id}`}
                        className={itemClass}
                        style={{ display: 'inline-flex', ...style }}
                        onClick={notClick ? undefined : this.onClick}
                    >
                        <TitleComponent {...this.props} />
                        {
                            isColorPanel
                                ? <DropdownColorPanel
                                    onVisibleChange={(visible) => {
                                        this.setState({ dropdownVisible: visible });
                                    }}
                                    containerRef={this.ref}
                                    idPreFix={idPreFix}
                                    dropdownHeight={dropdownHeight!}
                                    {...this.props} />
                                : <DropDownContent idPreFix={idPreFix} {...this.props} />
                        }
                    </div>
                </Tooltip>
            );
        }

        private onClick = (e: React.MouseEvent<HTMLDivElement>) => {
            if (this.props.canDoubleClick) {
            // 手动判断双击和单击事件
                this.count += 1;
                setTimeout(() => {
                    if (this.count === 1) {
                        this.handleClick(e);
                    } else if (this.count === 2) {
                        this.handleDoubleClick();
                    }
                    this.count = 0;
                }, 300);
            } else {
            // 没有双击事件，直接走单击逻辑。
                this.handleClick(e);
            }
        };

        private handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
            if (this.props.disabled) {
                return;
            }
            this.setState({ tooltipVisible: false });
            if (!this.props.disabled) {
                let value = (this.state.value !== null && this.state.value !== undefined)
                    ? this.state.value
                    : this.props.value;
                const options = {
                    e,
                    clickFrom: 'toolbar',
                };
                if (value !== null) {
                    if (typeof value === 'boolean') {
                        value = !value;
                    }
                    this.props.handleClick?.(this.props.id, value, options);
                } else {
                    this.props.handleClick?.(this.props.id, null, options);
                }
            }
        };

        private handleDoubleClick = () => {
            if (this.props.disabled) {
                return;
            }
            window.log.info('item-hoc double click');
            if (this.props.handleDoubleClick) {
                this.props.handleDoubleClick(this.props.id);
            }
        };

        private handleVisibleChange = (visible: boolean) => {
            const { id, selected } = this.props;
            // visible 为 true 时，id 为 add_slide 不弹出 hover
            // 同时当下拉菜单出现后将指针挪出再 hover 时需将 tooltipVisible 重设为 false
            // 因为用户可能点击其余空白收回菜单，不触发 @handleClick
            if (visible && (id === ActionType.ADD_SLIDE || selected)) {
                this.setState({ tooltipVisible: false });
                return;
            }

            this.setState({ tooltipVisible: visible });
        };

        private tooltipOffset = (id: string): number => {
            if (id === ActionType.UNDO) return 20;
            if (id === ActionType.FORMAT_BRUSH) return 60;
            return 0;
        };
    }
);
